import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
    ClipboardList, BookOpen, Calendar, BarChart2, GraduationCap, Bell,
    CreditCard, MessageSquare, Users, FileText,
    Bus, Library, UserPlus, Clock, Home, Award, BookMarked,
    Settings, PieChart, School
} from "lucide-react";
import studentMockup from "@/assets/platform/student-app-v3.webp";
import parentMockup from "@/assets/platform/parent-app-v3.webp";
import teacherMockup from "@/assets/platform/teacher-app-v3.webp";
import dashboardMockup from "@/assets/dashboard-mockup.webp";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useNearViewport } from "@/hooks/use-near-viewport";

// ─── App Definitions ──────────────────────────────────────────────────────────
const apps = [
    {
        id: "student",
        label: "Student App",
        color: "#000000",
        colorLight: "#f4f4f5",
        route: "/student-app",
        tagline: "Learning made easy — on any phone",
        description: "Students access their timetable, assignments, exam results, lesson materials, and announcements all from one mobile app.",
        appType: "mobile",
        mockup: studentMockup,
        features: [
            { icon: ClipboardList, label: "Assignments" },
            { icon: Calendar, label: "Timetable" },
            { icon: BarChart2, label: "Results" },
            { icon: BookOpen, label: "Lessons" },
            { icon: GraduationCap, label: "Exams" },
            { icon: Bell, label: "Announcements" },
            { icon: Home, label: "Attendance" },
            { icon: Library, label: "Library" },
        ],
    },
    {
        id: "parent",
        label: "Parent App",
        color: "#000000",
        colorLight: "#f4f4f5",
        route: "/parent-app",
        tagline: "Stay close to your child's school life",
        description: "Parents monitor attendance, track fee balances, view exam results, and receive real-time school announcements — all from their phone.",
        appType: "mobile",
        mockup: parentMockup,
        features: [
            { icon: Home, label: "Attendance" },
            { icon: CreditCard, label: "Fee Payment" },
            { icon: BarChart2, label: "Results" },
            { icon: MessageSquare, label: "Messages" },
            { icon: Bell, label: "Announcements" },
            { icon: BookMarked, label: "Homework" },
            { icon: Calendar, label: "Timetable" },
            { icon: FileText, label: "Report Card" },
        ],
    },
    {
        id: "teacher",
        label: "Teacher App",
        color: "#000000",
        colorLight: "#f4f4f5",
        route: "/teacher-app",
        tagline: "Manage your class from anywhere",
        description: "Teachers mark attendance, set assignments, grade exams, upload lessons, and communicate with parents all from a single mobile app.",
        appType: "mobile",
        mockup: teacherMockup,
        features: [
            { icon: Users, label: "Classes" },
            { icon: ClipboardList, label: "Attendance" },
            { icon: GraduationCap, label: "Exams" },
            { icon: Award, label: "Add Result" },
            { icon: BookOpen, label: "Lessons" },
            { icon: Bell, label: "Announcement" },
            { icon: Calendar, label: "Timetable" },
            { icon: FileText, label: "Topics" },
        ],
    },
    {
        id: "admin",
        label: "Admin Dashboard",
        color: "#000000",
        colorLight: "#f4f4f5",
        route: "/admin-dashboard",
        tagline: "Full school control — one web dashboard",
        description: "Administrators manage students, teachers, fees, timetables, reports, and every aspect of school operations from a powerful web panel.",
        appType: "web",
        mockup: dashboardMockup,
        features: [
            { icon: School, label: "Students" },
            { icon: Users, label: "Teachers" },
            { icon: CreditCard, label: "Fees" },
            { icon: PieChart, label: "Reports" },
            { icon: Clock, label: "Timetable" },
            { icon: UserPlus, label: "Admissions" },
            { icon: Bus, label: "Transport" },
            { icon: Settings, label: "Settings" },
        ],
    },
];

// ─── Orbit Ring Component ─────────────────────────────────────────────────────
function OrbitRing({ features, color, radius, duration }: {
    features: { icon: LucideIcon; label: string }[];
    color: string;
    radius: number;
    duration: number;
}) {
    const count = features.length;
    return (
        <div
            aria-hidden="true"
            className="absolute inset-0 flex items-center justify-center"
            style={{ top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}
        >
            {/* Dashed orbit ring */}
            <div
                className="absolute rounded-full border border-dashed opacity-30"
                style={{ width: radius * 2, height: radius * 2, borderColor: color }}
            />
            {/* Rotating wrapper */}
            <div
                className="absolute"
                style={{
                    width: radius * 2,
                    height: radius * 2,
                    animation: `orbit-spin ${duration}s linear infinite`,
                }}
            >
                {features.map((feature, index) => {
                    const angle = (index / count) * 360;
                    const rad = (angle * Math.PI) / 180;
                    const x = radius + radius * Math.sin(rad) - 28;
                    const y = radius - radius * Math.cos(rad) - 28;
                    return (
                        <div
                            key={feature.label}
                            className="absolute flex flex-col items-center gap-1"
                            style={{
                                left: x,
                                top: y,
                                animation: `orbit-counter ${duration}s linear infinite`,
                            }}
                        >
                            <div
                                className="w-11 h-11 rounded-xl flex items-center justify-center shadow-md bg-white dark:bg-slate-800"
                                style={{ border: `1.5px solid ${color}22` }}
                            >
                                <feature.icon style={{ color }} className="w-5 h-5" />
                            </div>
                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap leading-none">
                                {feature.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const AppShowcaseSection = ({ fixedAppId }: { fixedAppId?: string }) => {
    const fixedApp = fixedAppId ? apps.find((app) => app.id === fixedAppId) : undefined;
    const displayedApps = fixedApp ? [fixedApp] : apps;
    const showDesktopVisualization = useMediaQuery("(min-width: 1024px)");
    const { ref: sectionRef, isNearViewport } = useNearViewport<HTMLElement>();

    return (
        <section ref={sectionRef} aria-labelledby="platform-showcase-title" className="py-24 bg-slate-50 dark:bg-slate-900 overflow-hidden">
            {/* Orbit CSS keyframes */}
            <style dangerouslySetInnerHTML={{ __html: `
        @keyframes orbit-spin    { from { transform: rotate(0deg);   } to { transform: rotate(360deg);  } }
        @keyframes orbit-counter { from { transform: rotate(0deg);   } to { transform: rotate(-360deg); } }
        @media (prefers-reduced-motion: reduce) {
          [style*="orbit-spin"], [style*="orbit-counter"] { animation: none !important; }
        }
      ` }} />

            <div className="container mx-auto px-4 lg:px-8 max-w-7xl">
                {/* Section header */}
                <div className="text-center mb-14">
                    <span className="mb-4 inline-block rounded-full bg-black/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-black">
                        OUR PLATFORM
                    </span>
                    <h2 id="platform-showcase-title" className="text-3xl md:text-5xl font-heading font-extrabold text-slate-800 dark:text-slate-100 tracking-tight mb-4">
                        One Ecosystem. Every Role.
                    </h2>
                    <p className="text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto">
                        Apps for students, parents, and teachers — plus a powerful admin dashboard — all connected in one school management platform.
                    </p>
                </div>

                {/* Platform showcases */}
                <div className="space-y-24 lg:space-y-32">
                    {displayedApps.map((app, index) => (
                        <motion.div
                            key={app.id}
                            className={`flex flex-col items-center gap-12 lg:gap-20 ${index % 2 === 0 ? "lg:flex-row" : "lg:flex-row-reverse"
                                }`}
                        >
                            {/* Left: Description + CTA */}
                            <div className="lg:w-2/5 text-center lg:text-left order-2 lg:order-1">
                                <div
                                    className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-4"
                                    style={{ background: app.colorLight, color: app.color }}
                                >
                                    {app.appType === "mobile" ? "Mobile app" : "Web dashboard"}
                                </div>
                                <h3 className="text-3xl md:text-4xl font-extrabold text-slate-800 dark:text-slate-100 mb-4 leading-tight">
                                    {app.label}
                                </h3>
                                <p
                                    className="text-lg font-semibold mb-4"
                                    style={{ color: app.color }}
                                >
                                    {app.tagline}
                                </p>
                                <p className="text-slate-500 dark:text-slate-400 leading-relaxed mb-8">
                                    {app.description}
                                </p>

                                {/* Feature pill list */}
                                <ul className="flex flex-wrap gap-2 justify-center lg:justify-start mb-8">
                                    {app.features.map((feature) => (
                                        <li
                                            key={feature.label}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
                                            style={{ background: app.colorLight, color: app.color }}
                                        >
                                            <feature.icon aria-hidden="true" className="w-3.5 h-3.5" />
                                            {feature.label}
                                        </li>
                                    ))}
                                </ul>

                                <Link
                                    to={app.route}
                                    className="inline-flex min-h-11 items-center rounded-xl px-7 py-3 text-sm font-bold text-white shadow-lg transition-all hover:scale-105 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none motion-reduce:hover:scale-100"
                                    style={{ background: app.color }}
                                >
                                    Explore {app.label} <span aria-hidden="true">→</span>
                                </Link>
                            </div>

                            {/* Right: Orbiting phone/dashboard */}
                            <div className="hidden order-2 h-[480px] w-3/5 items-center justify-center lg:flex">
                                {showDesktopVisualization && isNearViewport && <div className="relative flex items-center justify-center" style={{ width: 480, height: 480 }}>
                                    {/* Orbit ring */}
                                    <OrbitRing
                                        features={app.features}
                                        color={app.color}
                                        radius={220}
                                        duration={18}
                                    />

                                    {/* Center glow */}
                                    <div
                                        aria-hidden="true"
                                        className="absolute rounded-full opacity-20 blur-3xl"
                                        style={{
                                            width: 260, height: 260,
                                            background: app.color,
                                        }}
                                    />

                                    {/* Phone / dashboard mockup */}
                                    <div className="relative z-10">
                                        {app.appType === "mobile" ? (
                                            /* iPhone frame */
                                            <div className="relative h-[394px] w-[190px] rounded-[42px] bg-[#09090b] p-[7px] shadow-[0_28px_60px_-20px_rgba(15,23,42,0.55)] ring-1 ring-black/80">
                                                {/* Side controls */}
                                                <span className="absolute -left-[3px] top-[78px] h-7 w-[3px] rounded-l-sm bg-slate-700" />
                                                <span className="absolute -left-[3px] top-[116px] h-11 w-[3px] rounded-l-sm bg-slate-700" />
                                                <span className="absolute -left-[3px] top-[170px] h-11 w-[3px] rounded-l-sm bg-slate-700" />
                                                <span className="absolute -right-[3px] top-[125px] h-16 w-[3px] rounded-r-sm bg-slate-700" />

                                                <div className="relative h-full w-full overflow-hidden rounded-[35px] bg-white ring-1 ring-white/15">
                                                    <img
                                                        src={app.mockup}
                                                        alt={`${app.label} screenshot`}
                                                        width={853}
                                                        height={1844}
                                                        loading="lazy"
                                                        decoding="async"
                                                        className="h-full w-full object-contain"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            /* Dashboard Mockup (No Card Wrapper) */
                                            <div className="relative w-80 h-auto">
                                                <img
                                                    src={app.mockup}
                                                    alt={`${app.label} screenshot`}
                                                    width={1280}
                                                    height={720}
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-full h-full object-cover rounded-xl shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)]"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>}
                            </div>
                        </motion.div>
                    ))}
                </div>

            </div>
        </section>
    );
};

export default AppShowcaseSection;
